/**
 * VRInputDevice - Manages a single VR input device (controller or hand)
 *
 * This class handles the lifecycle of a VR input device, listening for connected/disconnected
 * events and creating the appropriate handler based on device type. Uses clean abstractions
 * to eliminate branching logic.
 */

import * as THREE from 'three';

import { Delegate } from '../utils/Delegate.js';

import { VRControllerHandler } from './VRControllerHandler.js';
import { VRHandHandler } from './VRHandHandler.js';

import type { VRControllerOptions } from './VRControllerManager.js';
import type { VRDeviceHandler } from './VRDeviceHandler.js';
import type { World } from '../game/World.js';

export class VRInputDevice {
  private controllerIndex: number;
  private world: World;
  private renderer: THREE.WebGLRenderer;
  private options: Required<VRControllerOptions>;

  // Three.js objects
  private controller: THREE.Group;

  // Current device handler (clean abstraction - no branching!)
  private currentHandler: VRDeviceHandler | null = null;

  // Ray visualization
  private rayLine: THREE.Mesh | null = null;
  private rayHitSphere: THREE.Mesh | null = null;
  private maxRayDistance: number = 10;

  // Events
  public readonly onControllerConnected: Delegate<[XRInputSource], void> = new Delegate();
  public readonly onControllerDisconnected: Delegate<[XRInputSource], void> = new Delegate();
  public readonly onControllerUpdated: Delegate<[XRInputSource], void> = new Delegate();

  constructor(
    controllerIndex: number,
    world: World,
    renderer: THREE.WebGLRenderer,
    options: Required<VRControllerOptions>
  ) {
    this.controllerIndex = controllerIndex;
    this.world = world;
    this.renderer = renderer;
    this.options = options;
    this.maxRayDistance = options.maxRayDistance;

    // Get Three.js controller object
    this.controller = this.renderer.xr.getController(controllerIndex);

    // Set up event listeners
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    (this.controller as any).addEventListener('connected', (event: any) => {
      const inputSource = event.data as XRInputSource;
      this.handleControllerConnected(inputSource);
    });

    (this.controller as any).addEventListener('disconnected', (event: any) => {
      const inputSource = event.data as XRInputSource;
      this.handleControllerDisconnected(inputSource);
    });
  }

  private handleControllerConnected(inputSource: XRInputSource): void {
    console.log(`[VRInputDevice ${this.controllerIndex}] Controller connected: ${inputSource.handedness} ${inputSource.hand ? 'hand' : 'controller'}`);

    // Clean disposal of any existing handler
    if (this.currentHandler) {
      this.currentHandler.dispose();
      this.currentHandler = null;
    }

    // Create appropriate handler based on device type (no branching in handlers!)
    if (inputSource.hand) {
      this.currentHandler = new VRHandHandler(inputSource, this.renderer, this.world, this.controllerIndex);
    } else {
      this.currentHandler = new VRControllerHandler(inputSource, this.renderer, this.world, this.controllerIndex);
    }

    // Set up ray visualization now that device is connected
    this.createRayVisualization();

    this.onControllerConnected.invoke(inputSource);
  }

  private handleControllerDisconnected(inputSource: XRInputSource): void {
    console.log(`[VRInputDevice ${this.controllerIndex}] Controller disconnected: ${inputSource.handedness}`);

    if (this.currentHandler) {
      this.currentHandler.dispose();
      this.currentHandler = null;
    }

    // Clean up ray visualization when device disconnects
    this.cleanupRayVisualization();

    this.onControllerDisconnected.invoke(inputSource);
  }

  /** Update device state (call every frame) */
  public update(): void {
    if (!this.currentHandler || !this.renderer?.xr.isPresenting) {
      return;
    }

    this.updateRayVisualization();
    this.onControllerUpdated.invoke(this.currentHandler.getInputSource());
  }

  /** Get target ray pose for raycasting (extracted from target ray group) */
  public getTargetRayPose(): { origin: THREE.Vector3; direction: THREE.Vector3 } | null {
    const group = this.currentHandler?.getTargetRayGroup();
    if (!group) return null;

    // Force matrix update
    group.updateMatrixWorld(true);

    // Extract origin from group position
    const origin = new THREE.Vector3();
    origin.setFromMatrixPosition(group.matrixWorld);

    // Extract direction from group rotation (forward is -Z)
    const direction = new THREE.Vector3(0, 0, -1);
    direction.transformDirection(group.matrixWorld);

    return { origin, direction };
  }

  /** Get grip pose for object attachment (extracted from grip group) */
  public getGripPose(): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null {
    const group = this.currentHandler?.getGripGroup();
    if (!group) return null;

    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();

    group.getWorldPosition(position);
    group.getWorldQuaternion(quaternion);

    return { position, quaternion };
  }

  /** Get the Three.js target ray group (for pointing/raycasting) */
  public getTargetRayGroup(): THREE.Group | null {
    return this.currentHandler?.getTargetRayGroup() ?? null;
  }

  /** Get the Three.js grip group */
  public getGripGroup(): THREE.Group | null {
    return this.currentHandler?.getGripGroup() ?? null;
  }

  /** Get current input source */
  public getInputSource(): XRInputSource | null {
    return this.currentHandler?.getInputSource() ?? null;
  }

  /** Get controller index */
  public getControllerIndex(): number {
    return this.controllerIndex;
  }

  /** Get handedness of this device */
  public getHandedness(): 'left' | 'right' | 'none' {
    return this.currentHandler?.getHandedness() ?? 'none';
  }


  private createRayVisualization(): void {
    if (!this.options.rayVisualizationEnabled) return;

    // Clean up existing ray visualization first
    this.cleanupRayVisualization();

    // Create hit point sphere
    const sphereGeometry = new THREE.SphereGeometry(0.02, 8, 8);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00, // Will be updated based on handedness
      transparent: true,
      opacity: 0.9,
      depthTest: true, // Ensure sphere is always visible
      depthWrite: true
    });
    this.rayHitSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this.rayHitSphere.visible = false;
    this.rayHitSphere.renderOrder = 1000; // Render on top
    this.world.scene.add(this.rayHitSphere);

    // Create ray line as a thin cylinder (more reliable than Line in VR)
    const lineGeometry = new THREE.CylinderGeometry(0.001, 0.001, 1, 8);
    const lineMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00, // Will be updated based on handedness
      transparent: true,
      opacity: 0.8,
      depthTest: true,
      depthWrite: true
    });
    this.rayLine = new THREE.Mesh(lineGeometry, lineMaterial);
    this.rayLine.visible = false;
    this.rayLine.renderOrder = 1000; // Render on top
    this.world.scene.add(this.rayLine);
  }

  private cleanupRayVisualization(): void {
    if (this.rayHitSphere) {
      this.world.scene.remove(this.rayHitSphere);
      this.rayHitSphere.geometry.dispose();
      (this.rayHitSphere.material as THREE.Material).dispose();
      this.rayHitSphere = null;
    }

    if (this.rayLine) {
      this.world.scene.remove(this.rayLine);
      this.rayLine.geometry.dispose();
      (this.rayLine.material as THREE.Material).dispose();
      this.rayLine = null;
    }
  }

  private updateRayVisualization(): void {
    if (!this.options.rayVisualizationEnabled || !this.rayHitSphere || !this.rayLine) {
      return;
    }

    const rayData = this.getTargetRayPose();
    if (!rayData) {
      this.rayHitSphere.visible = false;
      this.rayLine.visible = false;
      return;
    }

    const { origin, direction } = rayData;
    const handedness = this.getHandedness();
    const color = handedness === 'left' ? 0x00ff00 : 0xff0000; // Green for left, red for right

    // Perform physics raycast
    const physicsEngine = this.world.gameLoop?.physicsEngine;
    if (!physicsEngine) {
      this.rayHitSphere.visible = false;
      this.rayLine.visible = false;
      return;
    }

    const hitResults = physicsEngine.performHitTest({
      origin,
      direction,
      maxDistance: this.maxRayDistance,
      stopOnFirstHit: true,
      collisionProfile: null,
      ignoredActors: [],
      ignoredComponents: []
    });

    let endPoint: THREE.Vector3;
    const shortRayDistance = 0.5; // Short ray when not hitting anything

    if (hitResults.length > 0) {
      // Hit something - show full ray to hit point
      const hitPoint = hitResults[0].hitLocation;
      this.rayHitSphere.position.copy(hitPoint);
      this.rayHitSphere.visible = true;
      endPoint = hitPoint.clone();

      // Update sphere color
      const sphereMaterial = this.rayHitSphere.material as THREE.MeshBasicMaterial;
      sphereMaterial.color.setHex(color);
      sphereMaterial.opacity = 0.8;
    } else {
      // No hit - show short ray
      this.rayHitSphere.visible = false;
      endPoint = origin.clone().add(direction.clone().multiplyScalar(shortRayDistance));
    }

    // Update ray line (cylinder) position and orientation
    const distance = origin.distanceTo(endPoint);
    const midPoint = origin.clone().add(endPoint).multiplyScalar(0.5);

    // Position the cylinder at the midpoint
    this.rayLine.position.copy(midPoint);

    // Scale the cylinder to match the ray length
    this.rayLine.scale.set(1, distance, 1);

    // Orient the cylinder to point from origin to endPoint
    this.rayLine.lookAt(endPoint);
    this.rayLine.rotateX(Math.PI / 2); // Cylinder is along Y axis, rotate to Z

    this.rayLine.visible = true;

    // Update line color
    const lineMaterial = this.rayLine.material as THREE.MeshBasicMaterial;
    lineMaterial.color.setHex(color);
    lineMaterial.opacity = 0.8;
  }

  /**
   * Enable or disable ray visualization
   */
  public setRayVisualizationEnabled(enabled: boolean): void {
    this.options.rayVisualizationEnabled = enabled;
    if (this.rayHitSphere) {
      this.rayHitSphere.visible = enabled && this.rayHitSphere.visible;
    }
    if (this.rayLine) {
      this.rayLine.visible = enabled && this.rayLine.visible;
    }
  }

  /**
   * Set controller model visibility (only works for motion controllers, not hands)
   */
  public setControllerVisible(visible: boolean): void {
    if (this.currentHandler instanceof VRControllerHandler) {
      this.currentHandler.setControllerVisible(visible);
    }
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this.currentHandler) {
      this.currentHandler.dispose();
      this.currentHandler = null;
    }

    this.cleanupRayVisualization();
  }
}